import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope } from "lucide-react";

export default function CompleteProfile() {
  const { user, loading, setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    specialty: user?.specialty || "",
    whatsapp: user?.whatsapp || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Verifying session…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "doctor") return <Navigate to="/dashboard" replace />;
  if ((user.specialty || "").trim() && (user.whatsapp || "").trim()) {
    return <Navigate to="/dashboard" replace />;
  }

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.put("/auth/complete-profile", {
        specialty: form.specialty.trim(),
        whatsapp: form.whatsapp.trim(),
      });
      setUser(data);
      toast.success("Profile completed");
      navigate("/dashboard/verification", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="complete-profile-page" className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-24">
      <div className="overline mb-3 flex items-center gap-2">
        <Stethoscope className="w-4 h-4" />
        Complete your doctor profile
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-tight">
        Tell us a bit more about you
      </h1>
      <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
        Before you can browse the catalog, please complete the mandatory fields below.
        These are required for license verification and order coordination.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="complete-profile-form">
        <div>
          <Label htmlFor="specialty">{t("reg.specialty")}</Label>
          <Input
            id="specialty"
            required
            value={form.specialty}
            onChange={update("specialty")}
            placeholder="Endocrinología, Medicina interna, …"
            className="mt-2 h-11"
            data-testid="complete-specialty"
          />
        </div>
        <div>
          <Label htmlFor="whatsapp">{t("reg.whatsapp")}</Label>
          <Input
            id="whatsapp"
            required
            type="tel"
            inputMode="tel"
            value={form.whatsapp}
            onChange={update("whatsapp")}
            placeholder="+1 809 555 1234"
            className="mt-2 h-11"
            data-testid="complete-whatsapp"
          />
          <p className="text-xs text-muted-foreground mt-1">{t("reg.whatsapp.hint")}</p>
        </div>

        {error && (
          <div data-testid="complete-error" className="text-sm text-destructive">
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-md"
          data-testid="complete-submit"
        >
          {submitting ? "Saving…" : "Continue"}
        </Button>
      </form>
    </div>
  );
}
